import { openSidebarSection, selectSettingsResolutionPreset } from '../../../../helpers/curatorActions';
import { wait } from '../../../../helpers/jsHelper';
import { dispatcher } from '../../../../helpers/projectHelper';
import { CURATOR_MENU_SECTION } from '../../../../redux/slicers/admin/curatorMenuPreferencesSlicer';
import {
  openSettingsSection,
  setCuratorSettingData,
  setMetadataIsRender,
  setSelectedGraphicQuality,
  setSelectedMeasureUnit,
  SETTINGS_SECTION,
  updateCameraSettings,
  updateResolutionPreset,
} from '../../../../redux/slicers/admin/curatorSlicer';
import { setColorPickerColorType } from '../../../../redux/slicers/admin/curatorStylesSlicer';
import { setGlobalHeight, setGlobalWidth } from '../../../../redux/slicers/camera/cameraSettings';

export class UndoRedoCuratorSettings {
  constructor({ undoRedoInstance, unityContext }) {
    this.undoRedo = undoRedoInstance;
    this.unityContext = unityContext;

    this.init();
  }

  _openSettingsSection = async (sectionName) => {
    await wait(20); // we wait some time for state to be updated in redux -> otherwise it causes bug when switching between the sections tries to save settings data and it causes old data to be displayed - https://app.clickup.com/t/2nvr2bj
    // update ui state so undo changes are visible
    const sectionOpened = openSidebarSection(CURATOR_MENU_SECTION.SETTINGS);

    if (!sectionOpened) {
      // TODO: handle this case -> sometimes we might prevent opening other sections if for example camera is not valid
    }
    dispatcher(openSettingsSection(sectionName));
  };

  _updateCameraSettings = async (settingsKey, value) => {
    const floatValue = parseFloat(value);
    dispatcher(updateCameraSettings({ settingsKey, value: floatValue }));

    switch (settingsKey) {
      case 'verticalRotationSpeed':
        this.undoRedo.unityContext.handleVerticalRotationSpeed(floatValue);
        break;
      case 'horizontalRotationSpeed':
        this.undoRedo.unityContext.handleHorizontalRotationSpeed(floatValue);
        break;
      case 'zoomSpeed':
        this.undoRedo.unityContext.cameraModule.OnUpdateCameraZoomSpeed(floatValue);
        break;
      case 'panSpeed':
        this.undoRedo.unityContext.cameraModule.OnUpdateCameraMoveSpeed(floatValue);
        break;
      default:
        throw new Error('Unable to update camera settings');
    }

    // this should go after state was updated -> as on section switch we're saving data on the backend
    await this._openSettingsSection(SETTINGS_SECTION.CAMERA_SETTINGS);
  };

  _updateResolutionPreset = async (data) =>{
    dispatcher(updateResolutionPreset(data));
    await this._openSettingsSection(SETTINGS_SECTION.RESOLUTION_PRESETS);
  }

  init = () => {
    // resolution presets
    this.updateResolutionPresetName = this.undoRedo.createAction(({ prevValue, nextValue, data }) => {
      return {
        prevValue, nextValue, data,
        undo: async () => {
          await this._updateResolutionPreset({
            id: data.id,
            name: prevValue,
          })
        },
        redo: async () => {
          await this._updateResolutionPreset({
              id: data.id,
              name: nextValue,
            })
        },
      };
    })

    // toggle lock
    this.toggleResolutionPresetLock = this.undoRedo.createAction(({ prevValue, nextValue, id }) => {
      return {
        prevValue, nextValue, id,
        undo: async () => {
          await this._updateResolutionPreset({ id, lock: prevValue, })
        },
        redo: async () => {
          await this._updateResolutionPreset({ id, lock: nextValue, })
        },
      };
    })

    this.selectResolutionPreset = this.undoRedo.createAction(({ selectedItem, nextSelectedItem }) => {
      return {
        selectedItem, nextSelectedItem,
        undo: async () => {
          selectSettingsResolutionPreset(selectedItem)
          await this._openSettingsSection(SETTINGS_SECTION.RESOLUTION_PRESETS);

          this.undoRedo.unityContext.cameraModule.changeSafeFrameResolution({ 
            cameraWidth: selectedItem.width,
            cameraHeight: selectedItem.height,
          })
        },
        redo: async () => {
          selectSettingsResolutionPreset(nextSelectedItem)
          await this._openSettingsSection(SETTINGS_SECTION.RESOLUTION_PRESETS);

          this.undoRedo.unityContext.cameraModule.changeSafeFrameResolution({ 
            cameraWidth: nextSelectedItem.width,
            cameraHeight: nextSelectedItem.height,
          })
        },
      };
    })

    this.updateResolution = this.undoRedo.createAction(({ id, prevValue, nextValue }) => {
      return {
        id, prevValue, nextValue,
        undo: async () => {
          await this._updateResolutionPreset({
            id,
            ...prevValue,
          })
          dispatcher(setGlobalWidth(prevValue.width));
          dispatcher(setGlobalHeight(prevValue.height));

          this.undoRedo.unityContext.cameraModule.changeSafeFrameResolution({ 
            cameraWidth: prevValue.width,
            cameraHeight: prevValue.height,
          })
        },
        redo: async () => {
          await this._updateResolutionPreset({
            id,
            ...nextValue,
          })
          dispatcher(setGlobalWidth(nextValue.width));
          dispatcher(setGlobalHeight(nextValue.height));

          this.undoRedo.unityContext.cameraModule.changeSafeFrameResolution({ 
            cameraWidth: nextValue.width,
            cameraHeight: nextValue.height,
          })
        },
      };
    })
    
    // camera settings
    this.updateCameraSettings = this.undoRedo.createAction(({ name, prevValue, nextValue }) => {
      return {
        name,
        prevValue,
        nextValue,
        undo: async () => {
          await this._updateCameraSettings(name, prevValue);
        },
        redo: async () => {
          await this._updateCameraSettings(name, nextValue);
        },
      };
    });

    this.selectGraphicQuality = this.undoRedo.createAction(({ prevId, nextId }) => {
      return {
        prevId,
        nextId,
        undo: async () => {
          dispatcher(setSelectedGraphicQuality({ id: prevId }));
          await this._openSettingsSection(SETTINGS_SECTION.GRAPHIC_QUALITY);
        },
        redo: async () => {
          dispatcher(setSelectedGraphicQuality({ id: nextId }));
          await this._openSettingsSection(SETTINGS_SECTION.GRAPHIC_QUALITY);
        },
      };
    });

    this.selectMeasureUnit = this.undoRedo.createAction(({ prevId, nextId }) => {
      return {
        prevId,
        nextId,
        undo: async () => {
          dispatcher(setSelectedMeasureUnit({ id: prevId }));
          await this._openSettingsSection(SETTINGS_SECTION.DEFAULT_MEASURE_UNITS);
        },
        redo: async () => {
          dispatcher(setSelectedMeasureUnit({ id: nextId }));
          await this._openSettingsSection(SETTINGS_SECTION.DEFAULT_MEASURE_UNITS);
        },
      };
    });


    // metadata
    this.toggleMetadata = this.undoRedo.createAction(({ prevValue, nextValue, data }) => {
      return {
        prevValue, nextValue, data,
        undo: async () => {
          dispatcher(setMetadataIsRender({ id: data.id, isRender: prevValue }))
          await this._openSettingsSection(SETTINGS_SECTION.METADATA_SETTINGS);
        },
        redo: async () => {
          dispatcher(setMetadataIsRender({ id: data.id, isRender: nextValue }))
          await this._openSettingsSection(SETTINGS_SECTION.METADATA_SETTINGS);
        },
      };
    })

    // settings 
    this.setMetadataSettings = this.undoRedo.createSimpleAction({
      changeStateAction: async (value) => {
        dispatcher(setCuratorSettingData(value))
        await this._openSettingsSection(SETTINGS_SECTION.METADATA_SETTINGS);
      },
    })

    // color picker color type 
    this.setColorPickerColorType = this.undoRedo.createSimpleAction({ 
      changeStateAction: (value) => {
        dispatcher(setColorPickerColorType(value))
      }
    })
  };
}
